// hooks/useDynamicCategories.js
import { useState, useCallback, useEffect, useRef } from "react";
import {
  doc,
  getDoc, // Needed for checking if the document exists
  updateDoc,
  arrayUnion, // No longer used for adding/overwriting categories
  arrayRemove,
  setDoc,
  deleteField, // Needed if the document doesn't exist
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  getCachedData,
  setCachedData,
  invalidateCache,
  incrementReadCount,
  getReadCount,
} from "../utils/cacheUtils";

const DOC_PATH = "staticContent/DynamicCategory";
const CACHE_KEY = DOC_PATH; // Cache the whole document

export const useDynamicCategories = () => {
  // Stores the entire { category: [brands], ... } object
  const [categoryBrandMap, setCategoryBrandMap] = useState({});
  // Stores just the category names (keys) for the dropdown
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    // Cleanup function to set isMounted to false when the component unmounts
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Helper to safely update state only if the component is still mounted
  const safeSetState = (setter, value) => {
    if (isMounted.current) {
      setter(value);
    }
  };

  // --- Fetch Data ---
  const fetchData = useCallback(async (forceRefetch = false) => {
    safeSetState(setLoading, true);
    safeSetState(setError, null);

    // --- Cache Check ---
    const cachedData = getCachedData(CACHE_KEY);
    if (cachedData !== null && !forceRefetch) {
      safeSetState(setCategoryBrandMap, cachedData);
      safeSetState(setCategories, Object.keys(cachedData).sort()); // Update categories from cache
      safeSetState(setLoading, false);
      console.log(
        `[CACHE] Used cached data for ${CACHE_KEY}. Total session reads: ${getReadCount()}`
      );
      return;
    }
    // --- End Cache Check ---

    const currentReadCount = incrementReadCount();
    console.log(`🔥 [READ #${currentReadCount}] Fetching ${DOC_PATH}`);

    try {
      const docRef = doc(db, DOC_PATH);
      const docSnap = await getDoc(docRef); // <<< ACTUAL FIRESTORE READ >>>

      let data = {};
      if (docSnap.exists()) {
        data = docSnap.data() || {};
        console.log(
          `✅ [READ #${currentReadCount}] Fetched data for ${DOC_PATH}. Total session reads: ${getReadCount()}`
        );
      } else {
        // Document doesn't exist, treat as empty but warn
        console.warn(
          `⚠️ [READ #${currentReadCount}] Document ${DOC_PATH} does not exist.`
        );
        // No need to setDoc here, the add function will handle creation if necessary
      }

      safeSetState(setCategoryBrandMap, data);
      safeSetState(setCategories, Object.keys(data).sort()); // Extract and sort category names
      setCachedData(CACHE_KEY, data); // Update cache
    } catch (err) {
      console.error(
        `❌ [READ #${currentReadCount}] Error fetching ${DOC_PATH}:`,
        err
      );
      safeSetState(
        setError,
        `Failed to fetch dynamic categories: ${err.message}`
      );
      safeSetState(setCategoryBrandMap, {}); // Reset data on error
      safeSetState(setCategories, []);
      invalidateCache(CACHE_KEY); // Invalidate cache on error
    } finally {
      safeSetState(setLoading, false);
    }
  }, []); // Dependency array is empty, fetchData itself doesn't change

  // --- Add/Update Brand to a Category (Implementing your logic) ---
  // This function now handles creating the doc/field or overwriting the field.
  // The UI component will handle the confirmation prompt BEFORE calling this.
  const addBrandToCategory = useCallback(
    async (category, selectedBrands) => {
      if (!category || !selectedBrands || selectedBrands.length === 0) {
        const errMsg = "Category and selected Brands cannot be empty.";
        safeSetState(setError, errMsg);
        console.error("Add Brand Error:", errMsg);
        // Use Promise.reject for consistent async error handling
        return Promise.reject(new Error(errMsg));
      }

      safeSetState(setLoading, true);
      safeSetState(setError, null);

      const categoryDocRef = doc(db, DOC_PATH);

      try {
        const docSnap = await getDoc(categoryDocRef);

        if (docSnap.exists()) {
          // Document exists, update or add the category field
          await updateDoc(categoryDocRef, {
            // This syntax adds the field if it doesn't exist,
            // and overwrites the array if it does exists.
            [category]: selectedBrands,
          });
          console.log(
            `✅ [UPDATE] Updated brands for category "${category}". Invalidating cache.`
          );
        } else {
          // Document does not exist, create it with the new category field
          await setDoc(categoryDocRef, {
            [category]: selectedBrands,
          });
          console.log(
            `✅ [SET] Created document "${DOC_PATH}" with category "${category}". Invalidating cache.`
          );
        }

        // Always invalidate cache and refetch after a successful write
        invalidateCache(CACHE_KEY);
        await fetchData(true); // Force refetch to update local state and cache

        return Promise.resolve(); // Resolve upon success
      } catch (err) {
        console.error(
          `❌ [WRITE] Error writing brands for category "${category}":`,
          err
        );
        safeSetState(setError, `Failed to save brands: ${err.message}`);
        // Do NOT refetch on error
        safeSetState(setLoading, false);
        throw err; // Re-throw error for the component to handle (e.g., show toast)
      }
      // Loading state is reset by the fetchData call in the success path
    },
    [fetchData] // Depend only on fetchData
  );

  // --- Delete Brand from a Category ---
  const deleteBrandFromCategory = useCallback(
    async (category, brandToDelete) => {
      if (!category || !brandToDelete) {
        const errMsg = "Category and Brand name must be provided for deletion.";
        safeSetState(setError, errMsg);
        console.error("Delete Brand Error:", errMsg);
        return Promise.reject(new Error(errMsg));
      }

      safeSetState(setLoading, true);
      safeSetState(setError, null);
      const upperCaseBrand = brandToDelete.toUpperCase().trim();

      try {
        const docRef = doc(db, DOC_PATH);

        // Use updateDoc with arrayRemove. This won't cause an error if the brand isn't in the array.
        // It will also not remove the category field if the array becomes empty.
        await updateDoc(docRef, {
          [category]: arrayRemove(upperCaseBrand),
        });

        console.log(
          `✅ [UPDATE] Removed brand "${upperCaseBrand}" from category "${category}". Invalidating cache.`
        );
        invalidateCache(CACHE_KEY);
        await fetchData(true); // Force refetch after update

        return Promise.resolve();
      } catch (err) {
        console.error(
          `❌ [UPDATE] Error deleting brand from category "${category}":`,
          err
        );
        safeSetState(setError, `Failed to delete brand: ${err.message}`);
        // Don't refetch on error
        safeSetState(setLoading, false);
        throw err; // Re-throw error
      }
      // Loading state reset by fetchData in success path
    },
    [fetchData] // Depend only on fetchData
  );

  const deleteAllBrandsFromCategory = useCallback(
    async (category) => {
      if (!category) {
        const errMsg = "Category name must be provided for deletion.";
        safeSetState(setError, errMsg);
        console.error("Delete Category Error:", errMsg);
        return Promise.reject(new Error(errMsg));
      }

      safeSetState(setLoading, true);
      safeSetState(setError, null);

      try {
        const docRef = doc(db, DOC_PATH);

        // Use deleteField to remove the entire category field
        await updateDoc(docRef, {
          [category]: deleteField(),
        });

        console.log(
          `✅ [UPDATE] Deleted category "${category}". Invalidating cache.`
        );
        invalidateCache(CACHE_KEY);
        await fetchData(true); // Force refetch to update local state and cache

        return Promise.resolve();
      } catch (err) {
        console.error(
          `❌ [UPDATE] Error deleting category "${category}":`,
          err
        );
        safeSetState(setError, `Failed to delete category: ${err.message}`);
        safeSetState(setLoading, false);
        throw err;
      }
    },
    [fetchData]
  );

  // --- Initial Fetch ---
  useEffect(() => {
    if (isMounted.current) {
      fetchData();
    }
  }, [fetchData]); // Run fetch on mount and if fetchData changes (it shouldn't due to useCallback)

  return {
    categoryBrandMap, // The full { category: [brands], ... } data
    categories, // Just the list of category names ['MOUSE', 'GRAPHICS CARD', ...]
    loading,
    error,
    fetchData, // Expose fetchData if manual refresh is needed
    addBrandToCategory, // Expose the new add function
    deleteBrandFromCategory,
    deleteAllBrandsFromCategory, // Expose the delete function
  };
};
